'use client'

import { useDict } from '@/lib'
import { useAuth } from '@clerk/nextjs'
import { useUserPermissions } from '@/hooks/useUserPermissions'
import { useTestsAndGamesHref } from '@/hooks/useTestsAndGamesHref'
import {
  buildNavbarMenuList,
  getAuthButtonHref,
  getAuthButtonLabelKey,
} from './navbar'
import type { NavbarMenuItem } from './types'

export interface NavbarConfig {
  menuList: NavbarMenuItem[]
  /** Null while labels are still loading. */
  authButton: { href: string; text: string } | null
}

/**
 * Assembles the full navbar configuration — menu items with labels + hrefs,
 * and the auth button — so that Navbar.tsx has zero routing/label logic.
 */
export function useNavbarConfig(): NavbarConfig {
  const dict = useDict('navbar')
  const testsAndGamesHref = useTestsAndGamesHref()
  const { isSignedIn, isLoaded } = useAuth()
  const { getHighestPriorityPanelUrl } = useUserPermissions()

  const authButtonHref = getAuthButtonHref({
    isSignedIn: !!isSignedIn,
    isLoaded,
    highestPriorityPanelUrl: getHighestPriorityPanelUrl(),
  })
  const authButtonLabelKey = getAuthButtonLabelKey({
    isSignedIn: !!isSignedIn,
    isLoaded,
  })

  if (!dict) return { menuList: [], authButton: null }

  return {
    menuList: buildNavbarMenuList(dict, { testsAndGamesHref }),
    authButton: {
      href: authButtonHref,
      text: dict[authButtonLabelKey] ?? 'Zaloguj się',
    },
  }
}
